import { useEffect, useLayoutEffect, useRef } from 'react';

// Use useLayoutEffect in DOM environments, useEffect in SSR
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Hook to lock body scroll when a modal or overlay is open.
 * Preserves scroll position and restores it when unlocked.
 * 
 * @param isLocked - Whether body scroll should be locked
 */
export function useBodyScrollLock(isLocked: boolean): void {
  const scrollYRef = useRef<number | null>(null);

  useIsomorphicLayoutEffect(() => {
    if (isLocked) {
      // Check if body is already locked
      if (document.body.style.position === 'fixed') {
        return;
      }

      // Save current scroll position
      const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      scrollYRef.current = scrollY;
      
      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      // Lock body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      
      // Add padding to replace scrollbar if needed to prevent layout shift
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    } else {
      // Unlock only if we have a stored scrollY (meaning we locked it)
      if (scrollYRef.current !== null) {
        const scrollY = scrollYRef.current;
        
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        document.body.style.paddingRight = '';
        
        // Restore scroll position
        window.scrollTo({
          top: scrollY,
          behavior: 'instant' as ScrollBehavior, // Force instant scroll
        });
        scrollYRef.current = null;
      }
    }

    // Cleanup on unmount - ONLY if we locked it
    return () => {
      if (scrollYRef.current !== null) {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        document.body.style.paddingRight = '';
        window.scrollTo({
            top: scrollYRef.current,
            behavior: 'instant' as ScrollBehavior, // Force instant scroll
        });
        scrollYRef.current = null;
      }
    };
  }, [isLocked]);
}

export default useBodyScrollLock;
